// components/TTSButton.tsx — small inline "Ascolta" button on Prof messages
// that reads the response aloud via Web Speech API SpeechSynthesis.
//
// Hidden entirely on browsers without SpeechSynthesis (rare in 2026 but
// possible behind strict privacy settings). When clicked, it cancels any
// currently-playing utterance so multiple Prof messages can't overlap audio.

import type { JSX } from 'react';
import { useTokens } from '../state/theme';
import { useSpeechSynthesis } from '../utils/useSpeechSynthesis';

export interface TTSButtonProps {
  text: string;
  /** Lang override — defaults to 'it-IT'. */
  lang?: string;
}

export function TTSButton({ text, lang = 'it-IT' }: TTSButtonProps): JSX.Element | null {
  const tokens = useTokens();
  const { supported, speaking, speak, stop } = useSpeechSynthesis();

  if (!supported) return null;
  const trimmed = text?.trim();
  if (!trimmed) return null;

  const label = speaking ? 'Ferma la lettura' : 'Ascolta la risposta';

  return (
    <button
      onClick={() => (speaking ? stop() : speak(trimmed, { lang }))}
      aria-label={label}
      aria-pressed={speaking}
      title={label}
      style={{
        display:        'inline-flex',
        alignItems:     'center',
        gap:            6,
        padding:        '4px 10px',
        minHeight:      28,
        background:     speaking ? tokens.accentSoft : 'transparent',
        border:         `1px solid ${speaking ? tokens.accent : tokens.line}`,
        borderRadius:   tokens.radius.pill,
        color:          speaking ? tokens.accentInk : tokens.textMuted,
        fontFamily:     tokens.fonts.body,
        fontSize:       11.5,
        fontWeight:     500,
        letterSpacing:  0.2,
        cursor:         'pointer',
        transition:     'background 120ms ease, color 120ms ease, border-color 120ms ease',
      }}
    >
      {speaking ? (
        // Stop icon — filled square.
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
          <rect x="1.5" y="1.5" width="7" height="7" rx="1.2" fill="currentColor" />
        </svg>
      ) : (
        // Speaker icon — outline + sound waves.
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 2.5L3.5 4.5H1.5v3H3.5L6 9.5V2.5z" fill="currentColor" fillOpacity="0.35" />
          <path d="M8.2 4.3c0.5 0.5 0.5 2.9 0 3.4" />
          <path d="M9.6 2.7c1.2 1.1 1.2 6.5 0 7.6" />
        </svg>
      )}
      {speaking ? 'In ascolto' : 'Ascolta'}
    </button>
  );
}
